import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

interface RequireAdminProps {
  children: React.ReactNode;
  redirectTo?: string;
}

/**
 * Guard Component für Admin-Berechtigung
 *
 * Im offenen Modus: Immer durchlassen (alle sind "Admin")
 * Im geschlossenen Modus: Nur echte Admins
 */
const RequireAdmin: React.FC<RequireAdminProps> = ({ children, redirectTo }) => {
  const { mode, isAdmin, user, loading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    // Nach dem Laden prüfen und ggf. umleiten
    if (!loading && mode === 'closed' && !isAdmin) {
      const destination = redirectTo || (user?.view_id ? `/${user.view_id}` : '/');
      console.log(`🔄 Non-admin user redirected from admin area to ${destination}`);
      navigate(destination, { replace: true });
    }
  }, [loading, mode, isAdmin, user, navigate, redirectTo]);

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Prüfe Admin-Berechtigung...</p>
        </div>
      </div>
    );
  }

  // Open mode - everyone has admin access
  if (mode === 'open') {
    return <>{children}</>;
  }

  // Closed mode - check admin status
  if (!isAdmin) {
    // Show redirect message briefly
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Weiterleitung zu Ihrer Ansicht...</p>
        </div>
      </div>
    );
  }

  // User is admin
  return <>{children}</>;
};

export default RequireAdmin;
